import { useState, useEffect, forwardRef, ForwardedRef, useImperativeHandle, useRef, ChangeEvent } from "react";
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Container from 'react-bootstrap/Container';
import Stack from 'react-bootstrap/Stack';
import Form from 'react-bootstrap/Form';
import Badge from 'react-bootstrap/Badge';
import Modal from 'react-bootstrap/Modal';
import ListGroup from 'react-bootstrap/ListGroup';
import FontDataService from "../services/font.service";
import IFontData from "../types/font.type";
import ToastMessage from "./toast.component";
import UploadFileStorage from "./upload-file.component";
import { DeleteFile } from '../storageAman';
import { ToastRef, FontModalRef, UploadFileRef } from "./interfaces";

interface IProps {
  onChangeFonts: (items: IFontData[]) => void;
};

const FontModal = forwardRef((props: IProps, ref: ForwardedRef<FontModalRef>) => {

  const unsubscribeRef = useRef<any>(null);
  const uploadFileRef = useRef<UploadFileRef>(null);
  const toastRef = useRef<ToastRef>(null);

  const [fonts, setFonts] = useState<IFontData[]>([]);
  const [currentFont, setCurrentFont] = useState<IFontData>({
    id: null,
    fontFamily: "",
    urls: [],
    createdTime: new Date(),
  });
  const [currentIndex, setCurrentIndex] = useState<number>(-1);
  const [validated, setValidated] = useState<boolean>(false);
  const [showModalFont, setShowModalFont] = useState<boolean>(false);

  useImperativeHandle(ref, () => ({
    showModal() {
      setShowModalFont(true);
      unsubscribeRef.current = FontDataService.getAll().orderBy("fontFamily", "asc").onSnapshot(onDataChange);
    }
  }));

  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) unsubscribeRef.current();
    }
  }, []);

  const onDataChange = (items: any) => {
    let datas = new Array<IFontData>();
    items.forEach((item: any) => {
      let id = item.id;
      let data = item.data();
      datas.push({
        id: id,
        fontFamily: data.fontFamily,
        urls: data.urls,
        createdTime: data.createdTime,
      });
    });
    setFonts(datas);
    props.onChangeFonts(datas);
  }

  const refreshList = () => {
    setCurrentFont({
      id: null,
      fontFamily: "",
      urls: [],
      createdTime: new Date(),
    });
    setCurrentIndex(-1);
  }

  const setActiveFont = (item: IFontData, index: number) => {
    setCurrentFont(item);
    setCurrentIndex(index);
  }

  const onChangeFontFamily = (e: ChangeEvent<HTMLInputElement>) => {
    const fontFamily = e.target.value;
    setCurrentFont({ ...currentFont, fontFamily });
  }

  const submitForm = (event: any) => {
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
    } else {
      event.preventDefault();
      event.stopPropagation();
      saveFont();
    }
    setValidated(true);
  }

  const saveFont = () => {
    const fileDatas = uploadFileRef.current?.getFiles();
    const urlDeletes = uploadFileRef.current?.getUrlDeletes();
    if (fileDatas && fileDatas.length > 0) {
      // Update
      if (currentFont.id) {
        const data = {
          id: currentFont.id,
          fontFamily: currentFont.fontFamily,
          urls: fileDatas,
          createdTime: currentFont.createdTime,
        };
        FontDataService.update(data.id, data)
          .then(() => {
            toastRef.current?.showMessage({
              variant: 'success',
              content: 'Update font successfully !'
            });
            if (urlDeletes && urlDeletes.length > 0) {
              urlDeletes.forEach(el => {
                DeleteFile(el, () => {}, () => {});
              });
            }
          })
          .catch((e: Error) => {
            toastRef.current?.showMessage({
              variant: 'danger',
              content: 'Update font unsuccessfully !'
            });
            console.log(e);
          });
      } else {
        // Create
        const data = {
          fontFamily: currentFont.fontFamily,
          urls: fileDatas,
          createdTime: new Date(),
        };
        FontDataService.create(data)
          .then(() => {
            toastRef.current?.showMessage({
              variant: 'success',
              content: 'Created new font successfully !'
            });
            refreshList();
          })
          .catch((e: Error) => {
            toastRef.current?.showMessage({
              variant: 'danger',
              content: 'Created new font unsuccessfully !'
            });
            console.log(e);
          });
      }
    } else {
      toastRef.current?.showMessage({
        variant: 'warning',
        content: 'Requires at least 1 font file !'
      });
    }
  }

  const deleteFont = (font: IFontData) => {
    if (font && font.id) {
      const urls = font.urls.map(item => item.fileUrl);
      FontDataService.delete(font.id)
        .then(() => {
          toastRef.current?.showMessage({
            variant: 'success',
            content: 'Delete font successfully !'
          });
          urls.forEach(el => {
            DeleteFile(el, () => {}, () => {});
          });
          refreshList();
        })
        .catch((e: Error) => {
          toastRef.current?.showMessage({
            variant: 'danger',
            content: 'Delete font unsuccessfully !'
          });
          console.log(e);
        });
    }
  }

  const handleAddNew = () => {
    refreshList();
  }

  const handleCloseModalFont = () => {
    refreshList();
    setShowModalFont(false);
    if (unsubscribeRef.current) unsubscribeRef.current();
  }

  return (
    <>
      <Modal size="xl" aria-labelledby="contained-modal-title-vcenter"
        centered show={showModalFont} onHide={handleCloseModalFont}>
        <Modal.Header closeButton>
          <Modal.Title>Font Manager</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Container fluid>
            <Row>
              <Col xs={4}>
                <Stack direction="horizontal" gap={3}>
                  <div>
                    <h4>List fonts</h4>
                  </div>
                  <div className="ms-auto" style={{ padding: '1rem 0rem' }}>
                    <Button type="button" variant="primary" onClick={handleAddNew}>
                      <i className="bi bi-plus-lg"></i> Add new
                    </Button>
                  </div>
                </Stack>
                <ListGroup as="ul" numbered>
                  {fonts && fonts.map((font, index) => (
                    <ListGroup.Item
                      key={`key-template-${index + 1}`}
                      as="li"
                      onClick={() => setActiveFont(font, index)}
                      active={index === currentIndex}
                      className="d-flex justify-content-between align-items-start"
                      style={{
                        cursor: 'pointer',
                      }}
                    >
                      <div className="ms-2 me-auto">
                        <div className="fw-bold">{font.fontFamily}</div>
                      </div>
                      <Badge
                        bg=""
                        pill
                        onClick={() => deleteFont(font)}
                      >
                        <i className="bi bi-x-circle-fill" style={{ color: '#dc3545', fontSize: '1rem' }}></i>
                      </Badge>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Col>
              <Col xs={8}>
                <Form noValidate validated={validated} onSubmit={submitForm}>
                  <Row className="mb-3">
                    <Form.Group as={Col} md="12" className="mb-3">
                      <Form.Label>Font Name*</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Type font name..."
                        value={currentFont.fontFamily}
                        onChange={onChangeFontFamily}
                        required />
                      <Form.Control.Feedback type="invalid">
                        Please enter a font name.
                      </Form.Control.Feedback>
                    </Form.Group>
                    <UploadFileStorage
                      ref={uploadFileRef}
                      fontId={currentFont.fontFamily.replace(/\s/g, "")}
                      disable={currentFont.fontFamily ? false : true}
                      files={currentFont.urls}
                    />
                  </Row>
                  <Row className="mb-3">
                    <Stack direction="horizontal" gap={2}>
                      <Button type="button" className="ms-auto" variant="secondary" onClick={handleCloseModalFont}>
                        Close
                      </Button>
                      <Button type="submit" variant="primary" style={{ marginLeft: '10px' }}>
                        {currentFont.id ? 'Save' : 'Add'}
                      </Button>
                    </Stack>
                  </Row>
                </Form>
              </Col>
            </Row>
          </Container>
        </Modal.Body>
      </Modal>
      <ToastMessage ref={toastRef} />
    </>
  );
});

export default FontModal;